import Phaser from 'phaser'
import { BaseGameMode, GameState } from '../game/core/BaseGameMode'
import { GameModeFactory } from '../game/core/GameModeFactory'
import { GameMode } from '../game/core/GameCore'
import { EndlessGameMode } from '../game/modes/EndlessGameMode'
import { TimeAttackGameMode } from '../game/modes/TimeAttackGameMode'
import { Grid } from '../game/Grid'
import { CellType } from '../game/CellTypes'
import { SettingsManager } from '../game/SettingsManager'
import { createTextStyle } from '../config/UIConfig'
import { GameStorage } from '../utils/GameStorage'
import { GameCenter } from '../native/GameCenter'
import { iOSIntegration } from '../native/iOSIntegration'
import { Analytics } from '../utils/Analytics'
import { IController, GameAction } from '../game/controllers/IController'
import { ControllerTelemetryService } from '../game/controllers/ControllerTelemetryService'
import { GameStateSaver } from '../utils/GameStateSaver'
import { ScreenshotMode } from '../utils/ScreenshotMode'
import { Capacitor } from '@capacitor/core'
import { ResponsiveLayout } from '../utils/ResponsiveLayout'
import { computeLayout, ComputedLayout, LayoutMode } from '../utils/layout'
import { DebugOverlay } from '../utils/DebugOverlay'

// Lazy loaded imports for optional systems
let GameHUD: any
let VisualEffectsController: any
let AudioController: any
let PerformanceMonitor: any
let PoolManager: any
let SoundEffect: any
let MusicTrack: any
let PooledSprite: any
let SwipeTapController: any
let OneHandedController: any
let DragRotateController: any
let MobilePerformanceOptimizer: any

export default class Game extends Phaser.Scene {
  private gameController!: BaseGameMode
  private grid!: Grid
  private gameHUD: any
  private cellLegend: any
  private visualEffects: any
  private audioController: any
  private performanceMonitor: any
  private poolManager: any
  private settingsManager: SettingsManager
  private touchController: IController | null = null
  private telemetryService: ControllerTelemetryService
  private mobileOptimizer: any
  private gridSprite!: Phaser.GameObjects.Image
  private cellSprites: Map<string, any>
  private lastUpdate: number
  private performanceMonitorEnabled: boolean = false
  private pauseDialogElements: Phaser.GameObjects.GameObject[] = []
  private selectedMode: GameMode = GameMode.ENDLESS

  // Layout system
  private currentLayout!: ComputedLayout
  private debugOverlay!: DebugOverlay

  // Time tracking
  private gameStartTime: number = 0
  private currentGameTime: number = 0
  private gameTimeTimer?: Phaser.Time.TimerEvent

  // Auto-save system
  private autoSaveInterval?: Phaser.Time.TimerEvent
  private lastAutoSave: number = 0
  private autoSaveFrequency: number = 30000 // Auto-save every 30 seconds
  private browserEventCleanup?: () => void
  private shouldLoadSavedGame: boolean = false

  constructor() {
    super('Game')
    this.cellSprites = new Map()
    this.lastUpdate = 0
    this.settingsManager = SettingsManager.getInstance()
    this.telemetryService = ControllerTelemetryService.getInstance()
  }

  /**
   * Initialize the scene with data
   */
  init(data: { loadSavedGame?: boolean; selectedMode?: GameMode } = {}) {
    this.shouldLoadSavedGame = data.loadSavedGame || false
    this.selectedMode = data.selectedMode || GameMode.ENDLESS
    console.log('Game scene: Initialize with loadSavedGame =', this.shouldLoadSavedGame, 'selectedMode =', this.selectedMode)
  }

  /**
   * Get the game controller (keeping for backwards compatibility but no longer needed)
   * @deprecated Use this.gameController directly
   */
  private get endlessMode(): EndlessGameMode {
    return this.gameController as EndlessGameMode
  }

  /**
   * Dynamically loads optional systems to reduce initial bundle size
   */
  private async loadOptionalSystems() {
    try {
      // Load UI systems
      const { GameHUD } = await import('../ui/GameHUD')
      const { CellLegend } = await import('../ui/CellLegend')

      // Load game systems
      const [
        { VisualEffectsController },
        { AudioController, SoundEffect, MusicTrack },
        { PerformanceMonitor },
        { PoolManager, PooledSprite }
      ] = await Promise.all([
        import('../game/VisualEffectsController'),
        import('../game/AudioController'),
        import('../utils/PerformanceMonitor'),
        import('../utils/ObjectPool')
      ])

      // Load mobile systems (temporarily always load for testing)
      let loadedSwipeTapController = null
      let loadedOneHandedController = null
      let loadedDragRotateController = null
      let loadedButtonController = null
      let loadedMobileOptimizer = null

      // if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
      const [
        { SwipeTapController: SwipeTapClass },
        { OneHandedController: OneHandedClass },
        { DragRotateController: DragRotateClass },
        { ButtonController: ButtonClass },
        { MobilePerformanceOptimizer: MobileOptimizerClass }
      ] = await Promise.all([
        import('../game/controllers/SwipeTapController'),
        import('../game/controllers/OneHandedController'),
        import('../game/controllers/DragRotateController'),
        import('../game/controllers/ButtonController'),
        import('../utils/MobilePerformanceOptimizer')
      ])

      loadedSwipeTapController = SwipeTapClass
      loadedOneHandedController = OneHandedClass
      loadedDragRotateController = DragRotateClass
      loadedButtonController = ButtonClass
      loadedMobileOptimizer = MobileOptimizerClass
        // }

        // Store constructors globally for use
        ; (globalThis as any).GameHUD = GameHUD
        ; (globalThis as any).CellLegend = CellLegend
        ; (globalThis as any).VisualEffectsController = VisualEffectsController
        ; (globalThis as any).AudioController = AudioController
        ; (globalThis as any).PerformanceMonitor = PerformanceMonitor
        ; (globalThis as any).PoolManager = PoolManager
        ; (globalThis as any).SoundEffect = SoundEffect
        ; (globalThis as any).MusicTrack = MusicTrack
        ; (globalThis as any).PooledSprite = PooledSprite
        ; (globalThis as any).SwipeTapController = loadedSwipeTapController
        ; (globalThis as any).OneHandedController = loadedOneHandedController
        ; (globalThis as any).DragRotateController = loadedDragRotateController
        ; (globalThis as any).ButtonController = loadedButtonController
        ; (globalThis as any).MobilePerformanceOptimizer = loadedMobileOptimizer

      // Initialize systems
      this.visualEffects = new VisualEffectsController(this)
      this.audioController = new AudioController(this)

      // Initialize settings and apply them to audio controller
      this.settingsManager.setAudioController(this.audioController)

      this.performanceMonitor = new PerformanceMonitor(this)
      this.performanceMonitorEnabled = false
      this.performanceMonitor.enable(this.performanceMonitorEnabled) // Performance overlay disabled by default (toggle with "." key)
      this.poolManager = new PoolManager(this)

      // Initialize mobile systems if available
      if (loadedMobileOptimizer) {
        this.mobileOptimizer = new loadedMobileOptimizer(this)
        console.log('Mobile Performance Optimizer initialized:', this.mobileOptimizer.getDeviceInfo())
      }

      // Initialize controller based on settings
      if (loadedSwipeTapController || loadedOneHandedController || loadedDragRotateController || loadedButtonController) {
        const scheme = this.settingsManager.getControllerScheme()
        let ControllerClass = null

        switch (scheme) {
          case 'Swipe+Tap':
            ControllerClass = loadedSwipeTapController
            break
          case 'OneHanded':
            ControllerClass = loadedOneHandedController
            break
          case 'Drag+Rotate':
            ControllerClass = loadedDragRotateController
            break
          case 'Button Controls':
            ControllerClass = loadedButtonController
            break
          default:
            ControllerClass = loadedSwipeTapController // Default fallback
        }

        if (ControllerClass) {
          this.touchController = new ControllerClass(this, this.gameController)
          this.touchController.onEnable()

          // Apply settings
          const hapticEnabled = this.settingsManager.isHapticFeedbackEnabled()
          this.touchController.setHapticEnabled(hapticEnabled)

          // Start telemetry session
          this.telemetryService.startSession(this.touchController.schemeName)

          console.log('Controller enabled:', this.touchController.schemeName)
        }
      }

      // Pre-create pools for all cell types
      const cellTypes = ['red', 'blue', 'green', 'yellow', 'purple', 'platelets']
      cellTypes.forEach(type => {
        this.poolManager.getSpritePool(`${type}_blink`, 15) // 15 sprites per type (for breathing animation)
        this.poolManager.getSpritePool(`${type}_clear`, 10)
      })

    } catch (error) {
      console.warn('Failed to load optional systems:', error)
      // Fallback: create mock objects to prevent errors
      this.visualEffects = {
        playEffect: () => { },
        destroy: () => { },
        createParticleEffect: () => { },
        createMatchEffect: () => { },
        createScreenShake: () => { }, // Disabled - too jarring for users
        createFloatingText: () => { },
        createHealingEffect: () => { },
        createComboEffect: () => { },
        createLevelUpEffect: () => { },
        createFadeTransition: () => Promise.resolve(),
        createScreenFlash: () => { }
      }
      this.audioController = {
        playSound: () => { },
        playMusic: () => { },
        destroy: () => { },
        stopMusic: () => { },
        playMatchSound: () => { },
        playCellSound: () => { }
      }
      this.performanceMonitor = { enable: () => { }, update: () => { }, destroy: () => { } }
      this.poolManager = { getSpritePool: () => ({ get: () => null, release: () => { } }) }
    }
  }

  /**
   * Calculates responsive layout for different screen sizes using centralized layout system
   */
  private calculateResponsiveLayout(width: number, height: number) {
    const layoutMode = this.settingsManager.getLayoutModeWithAutoDetect()
    return computeLayout(width, height, layoutMode)
  }

  /**
   * Handles window resize for responsive layout
   */
  private handleResize(gameSize: Phaser.Structs.Size) {
    const { width, height } = gameSize
    const layout = this.calculateResponsiveLayout(width, height)
    this.currentLayout = layout

    // Update grid position and scale
    if (this.gridSprite) {
      this.gridSprite.setPosition(layout.grid.x, layout.grid.y)
      this.gridSprite.setScale(layout.grid.scale)
      // Maintain grid visibility setting after resize
      const gridVisible = this.settingsManager.isGridVisible()
      this.gridSprite.setVisible(gridVisible)
    }

    // Update HUD position and configuration
    if (this.gameHUD) {
      this.gameHUD.updateLayout({
        position: layout.hud.position,
        scale: layout.hud.scale,
        width: layout.hud.width,
        height: layout.hud.height,
        mode: layout.mode, // Pass layout mode on resize
        pauseButton: layout.pauseButton, // Pass pause button position
        safeArea: layout.safeArea // Pass safe area insets
      })
    }

    // Update CellLegend position and configuration
    if (this.cellLegend) {
      this.cellLegend.updateLayout({
        position: layout.legend.position,
        scale: layout.legend.scale,
        width: layout.legend.width,
        height: layout.legend.height,
        mode: layout.mode // Pass layout mode on resize
      })
    }

    // Update touch controller zones for new size
    if (this.touchController) {
      this.touchController.resize(width, height)
    }

    // Update debug overlay
    if (this.debugOverlay) {
      this.debugOverlay.update(layout)
    }
  }

  async create() {
    console.log('Game scene: Starting create() method')

    try {
      const w = this.scale.width
      const h = this.scale.height

      console.log('Game scene: Setting up layout for', w, 'x', h)

      // Calculate responsive layout
      const layout = this.calculateResponsiveLayout(w, h)

      // Setup background - use selected background from settings
      const backgroundSetting = this.settingsManager.getBackgroundImage()
      const backgroundKey = backgroundSetting === 'default' ? 'bg' : `bg-${backgroundSetting}`
      const gameBg = this.add.image(w / 2, h / 2, backgroundKey)
      //        .setAlpha(0.25)
      // Scale to cover entire screen without black bars
      const bgWidth = this.registry.get('bgWidth') || 1080
      const bgHeight = this.registry.get('bgHeight') || 1920
      const scaleX = w / bgWidth
      const scaleY = h / bgHeight
      gameBg.setScale(Math.max(scaleX, scaleY))

      // Setup grid with proper positioning
      this.gridSprite = this.add.image(layout.grid.x, layout.grid.y, 'grid')
      this.gridSprite.setScale(layout.grid.scale)

      // Apply grid visibility setting
      const gridVisible = this.settingsManager.isGridVisible()
      this.gridSprite.setVisible(gridVisible)

      // Check if we need to restore game mode from saved game BEFORE creating the controller
      if (this.shouldLoadSavedGame && GameStateSaver.hasSavedGame()) {
        const savedGameState = GameStateSaver.loadGame()
        if (savedGameState) {
          // Restore the game mode from the saved state
          const savedMode = savedGameState.gameMode
          if (savedMode === 'Time Attack') {
            this.selectedMode = GameMode.TIME_ATTACK
          } else {
            this.selectedMode = GameMode.ENDLESS
          }
          console.log('Game scene: Restored game mode from save before creating controller:', this.selectedMode)
        }
      }

      // Initialize game controller (core system, load immediately)
      // This must happen AFTER we've determined the correct mode from the saved game
      this.gameController = GameModeFactory.createMode(this.selectedMode)
      this.grid = this.gameController.getGrid()

      // Dynamically load optional systems
      await this.loadOptionalSystems()

      // Initialize game HUD with responsive positioning
      console.log('Game scene: Initializing GameHUD')
      const GameHUDConstructor = (globalThis as any).GameHUD
      console.log('Game scene: GameHUD constructor available:', !!GameHUDConstructor)

      if (GameHUDConstructor) {
        try {
          this.gameHUD = new GameHUDConstructor(this, this.gameController, {
            position: layout.hud.position,
            scale: layout.hud.scale,
            width: layout.hud.width,
            height: layout.hud.height,
            mode: layout.mode, // Pass layout mode
            pauseButton: layout.pauseButton, // Pass pause button position
            safeArea: layout.safeArea // Pass safe area insets
          })
          console.log('Game scene: GameHUD initialized successfully')
        } catch (error) {
          console.error('Game scene: Failed to initialize GameHUD:', error)
          this.gameHUD = null
        }
      } else {
        console.error('Game scene: GameHUD constructor not available')
        this.gameHUD = null
      }

      // Initialize cell legend (left sidebar or bottom band)
      console.log('Game scene: Initializing CellLegend')
      console.log('Game scene: CellLegend layout config:', layout.legend)
      const CellLegendConstructor = (globalThis as any).CellLegend
      console.log('Game scene: CellLegend constructor available:', !!CellLegendConstructor)

      if (CellLegendConstructor) {
        try {
          // Enable drawer mode for grid-first layouts (topBottomHUD, overlayHUD)
          // Portrait mode always shows the legend (no drawer)
          const isGridFirstLayout = layout.mode === 'topBottomHUD' || layout.mode === 'overlayHUD'

          this.cellLegend = new CellLegendConstructor(this, {
            position: layout.legend.position,
            scale: layout.legend.scale,
            width: layout.legend.width,
            height: layout.legend.height,
            mode: layout.mode, // Pass layout mode
            drawerMode: isGridFirstLayout // Enable drawer mode for grid-first layouts only
          }, this.selectedMode) // Pass game mode to filter cell types
          console.log('Game scene: CellLegend initialized successfully with drawerMode:', isGridFirstLayout)
        } catch (error) {
          console.error('Game scene: Failed to initialize CellLegend:', error)
          this.cellLegend = null
        }
      } else {
        console.error('Game scene: CellLegend constructor not available')
      }

      // Initialize debug overlay
      this.debugOverlay = new DebugOverlay(this)
      this.currentLayout = layout

      // Enable debug overlay if setting is enabled
      if (this.settingsManager.isDebugOverlayEnabled()) {
        this.debugOverlay.setVisible(true)
        this.debugOverlay.update(layout)
      }

      // Setup animations for all cell types
      this.setupCellAnimations()

      // Setup input handling
      this.setupInputHandling()

      // Setup game event listeners
      this.setupGameEventListeners()

      // Setup resize handler for responsive layout
      this.scale.on('resize', this.handleResize, this)

      // Start the game - either load saved game or start fresh
      if (this.shouldLoadSavedGame && GameStateSaver.hasSavedGame()) {
        console.log('Game scene: Loading saved game')
        const savedGameState = GameStateSaver.loadGame()
        if (savedGameState) {
          // Mode was already restored before creating the controller
          this.gameController.restoreState(savedGameState)
          console.log('Game scene: Saved game restored successfully')

          // Update HUD to display restored state
          if (this.gameHUD) {
            this.gameHUD.updateDisplay()
            console.log('Game scene: HUD updated with restored state')
          }

          // Automatically resume the game since user clicked "Continue Game"
          this.gameController.resumeGame()
          console.log('Game scene: Resumed game after restore')
        } else {
          console.warn('Game scene: Failed to load saved game, starting fresh')
          this.gameController.startGame()
        }
      } else {
        console.log('Game scene: Starting fresh game')
        this.gameController.startGame()
      }
      this.lastUpdate = this.time.now

      // Setup auto-save system
      this.setupAutoSave()

      // Setup browser lifecycle events for emergency saves
      this.setupBrowserLifecycleEvents()

      // Start background music if user clicked New Game or Continue Game (required by browsers)
      const shouldStartMusic = this.registry.get('startMusic')
      if (shouldStartMusic && this.audioController) {
        const MusicTrackConstructor = (globalThis as any).MusicTrack
        if (MusicTrackConstructor) {
          console.log('Game scene: Starting music after user interaction from Title screen')
          this.audioController.playMusic(MusicTrackConstructor.GAMEPLAY)
          // Clear the flag so it doesn't auto-play again if returning to Game scene
          this.registry.set('startMusic', false)
        }
      }

      // Force layout refresh to ensure proper initial positioning
      console.log('Game scene: Forcing layout refresh for proper initialization')
      const refreshLayout = this.calculateResponsiveLayout(w, h)

      if (this.gameHUD) {
        this.gameHUD.updateLayout({
          position: refreshLayout.hud.position,
          scale: refreshLayout.hud.scale,
          width: refreshLayout.hud.width,
          height: refreshLayout.hud.height
        })
      }

      if (this.cellLegend) {
        this.cellLegend.updateLayout({
          position: refreshLayout.legend.position,
          scale: refreshLayout.legend.scale,
          width: refreshLayout.legend.width,
          height: refreshLayout.legend.height
        })
      }

      console.log('Game scene: Create method completed successfully')

      // Add floating screenshot button (visible on iOS/mobile)
      // this.addScreenshotButton()

      // Setup legend drawer event handlers
      this.events.on('showLegendDrawer', () => {
        if (this.cellLegend && this.cellLegend.isDrawerOpened && !this.cellLegend.isDrawerOpened()) {
          // Pause the game when drawer opens
          const gameState = this.gameController.getState()
          if (gameState === 'playing' || gameState === 'falling' || gameState === 'matching') {
            this.gameController.pauseGame()
          }

          // Open the drawer with a callback to resume game when closed
          this.cellLegend.openDrawer(() => {
            // Resume the game when drawer is closed
            this.gameController.resumeGame()
          })
        }
      })

      // Listen for scene resume event to ensure interactive elements work after pause/resume
      this.events.on('resume', () => {
        // On iOS, interactive elements may need to be re-enabled after scene pause/resume
        if (this.touchController) {
          // Re-enable the controller to restore interactive state on iOS                                     
          this.touchController.onDisable()
          this.touchController.onEnable()
        }
      })

    } catch (error) {
      console.error('Game scene: Error in create() method:', error)

      // Type guard for error handling
      const errorDetails = error instanceof Error ? {
        message: error.message,
        stack: error.stack,
        name: error.name
      } : { message: String(error), stack: 'N/A', name: 'Unknown' }

      console.error('Error details:', errorDetails)

      // Show detailed error message to user
      const errorMsg = errorDetails.message || 'Unknown error'
      this.add.text(this.scale.width / 2, this.scale.height / 2,
        `Game failed to load: ${errorMsg}\nCheck console for details\nPress F12 to open developer tools`,
        createTextStyle('label', {
          fontSize: '14px',
          color: '#ff0000',
          align: 'center',
          wordWrap: { width: this.scale.width - 40 }
        })
      ).setOrigin(0.5)
    }
  }

  update() {
    const currentTime = this.time.now
    const deltaTime = currentTime - this.lastUpdate
    this.lastUpdate = currentTime

    // Update game controller (with safety check)
    if (!this.gameController) {
      console.error('Game controller not initialized')
      return
    }

    // Process controller actions
    if (this.touchController) {
      this.touchController.update(deltaTime)
      this.processControllerActions()
    }

    this.gameController.update(deltaTime)

    // Update HUD display (with safety check)
    if (this.gameHUD && this.gameHUD.updateDisplay) {
      this.gameHUD.updateDisplay()
    }

    // Update visual representation
    this.updateCellSprites()
    this.updateCurrentPairSprites()

    // Update performance metrics with game-specific data
    this.updatePerformanceMetrics()

    // Update mobile performance optimizer if available
    if (this.mobileOptimizer) {
      const fps = this.game.loop.actualFps
      this.mobileOptimizer.updatePerformanceMetrics(fps)
    }
  }

  /**
   * Process buffered actions from the controller
   */
  private processControllerActions(): void {
    if (!this.touchController) return

    const actions = this.touchController.actions.drain()
    actions.forEach(actionEvent => {
      const handled = this.gameController.handleInput(actionEvent.action)

      // Record action in telemetry
      if (handled) {
        this.telemetryService.recordAction(actionEvent.action)
      }
    })
  }

  private setupCellAnimations() {
    const cellTypes = ['red', 'blue', 'green', 'yellow', 'purple', 'platelets']

    console.log('[Animation] Setting up cell animations for types:', cellTypes)

    cellTypes.forEach(type => {
      // Blink animation - uses the 8-frame sprite sheet
      this.anims.create({
        key: `${type}_blink`,
        frames: this.anims.generateFrameNumbers(`${type}_blink`, { start: 0, end: 7 }),
        frameRate: 8, // 8 frames per second = 1 second for full animation
        repeat: -1 // Loop forever
      })
      console.log(`[Animation] Created blink animation for ${type}`)

      // Breathing/blinking animation (alias) - uses the 8-frame sprite sheet
      this.anims.create({
        key: `${type}_breathe`,
        frames: this.anims.generateFrameNumbers(`${type}_blink`, { start: 0, end: 7 }),
        frameRate: 8, // 8 frames per second = 1 second for full animation
        repeat: -1 // Loop forever
      })
      console.log(`[Animation] Created breathe animation for ${type}`)

      // Bounce animation - simplified to use single frame (no atlas)
      this.anims.create({
        key: `${type}_bounce`,
        frames: [{ key: `${type}_bounce`, frame: 0 }],
        frameRate: 10,
        repeat: -1
      })

      // Pop animation - simplified to use single frame (no atlas)
      this.anims.create({
        key: `${type}_pop`,
        frames: [{ key: `${type}_pop`, frame: 0 }],
        frameRate: 12,
        repeat: 0
      })
    })

    console.log('[Animation] All cell animations created successfully')
  }

  private setupInputHandling() {
    // Keyboard controls
    const cursors = this.input.keyboard!.createCursorKeys()
    const wasd = this.input.keyboard!.addKeys('W,S,A,D,SPACE,ENTER,P')

    this.input.keyboard!.on('keydown', (event: KeyboardEvent) => {
      const SoundEffectConstructor = (globalThis as any).SoundEffect

      switch (event.code) {
        case 'KeyA':
        case 'ArrowLeft':
          this.gameController.handleInput('moveLeft')
          if (this.audioController && SoundEffectConstructor && SoundEffectConstructor.CELL_MOVE) {
            this.audioController.playSound(SoundEffectConstructor.CELL_MOVE)
          }
          break
        case 'KeyD':
        case 'ArrowRight':
          // D key: move right if no modifier, debug overlay if Shift pressed
          if (event.shiftKey) {
            // Shift+D: Toggle debug overlay
            if (this.debugOverlay) {
              this.debugOverlay.toggle()
              const isEnabled = this.debugOverlay.getVisible()
              this.settingsManager.setDebugOverlayEnabled(isEnabled)
              if (isEnabled && this.currentLayout) {
                this.debugOverlay.update(this.currentLayout)
              }
            }
          } else {
            // Just D: move right
            this.gameController.handleInput('moveRight')
            if (this.audioController && SoundEffectConstructor && SoundEffectConstructor.CELL_MOVE) {
              this.audioController.playSound(SoundEffectConstructor.CELL_MOVE)
            }
          }
          break
        case 'KeyS':
          // S key: soft drop if no modifier, screenshot manager if Shift pressed
          if (event.shiftKey) {
            // Shift+S: Open Screenshot Manager (easier on iOS simulator)
            if (!this.scene.isActive('ScreenshotManager')) {
              console.log('📸 Opening Screenshot Manager (Shift+S)')
              this.scene.launch('ScreenshotManager')
            } else {
              this.scene.stop('ScreenshotManager')
            }
          } else {
            // Just S: soft drop
            this.gameController.handleInput('softDrop')
            if (this.audioController && SoundEffectConstructor && SoundEffectConstructor.CELL_DROP) {
              this.audioController.playSound(SoundEffectConstructor.CELL_DROP)
            }
          }
          break
        case 'ArrowDown':
          this.gameController.handleInput('softDrop')
          if (this.audioController && SoundEffectConstructor && SoundEffectConstructor.CELL_DROP) {
            this.audioController.playSound(SoundEffectConstructor.CELL_DROP)
          }
          break
        case 'KeyW':
        case 'ArrowUp':
        case 'Space':
          this.gameController.handleInput('rotateClockwise')
          if (this.audioController && SoundEffectConstructor && SoundEffectConstructor.CELL_ROTATE) {
            this.audioController.playSound(SoundEffectConstructor.CELL_ROTATE)
          }
          break
        case 'KeyP':
          // Handle pause with dialog
          if (this.gameController.getState() === GameState.PAUSED) {
            // If already paused, resume (dialog will be destroyed by resume button)
            this.resumeFromPause()
          } else {
            // Pause the game (event listener will show dialog)
            this.gameController.handleInput('pause')
            if (this.audioController && SoundEffectConstructor && SoundEffectConstructor.PAUSE) {
              this.audioController.playSound(SoundEffectConstructor.PAUSE)
            }
          }
          break
        case 'Period':
          // Toggle performance monitor with "." key
          if (this.performanceMonitor) {
            this.performanceMonitorEnabled = !this.performanceMonitorEnabled
            this.performanceMonitor.enable(this.performanceMonitorEnabled)
            console.log('Performance monitor:', this.performanceMonitorEnabled ? 'enabled' : 'disabled')
          }
          break
        case 'KeyT':
          // Toggle touch zone debug with "T" key
          if (this.touchController) {
            // Since IController doesn't have toggleDebugMode, we need to track state
            const currentDebug = (this.touchController as any)._debugMode || false
            this.touchController.setDebugMode(!currentDebug)
              ; (this.touchController as any)._debugMode = !currentDebug
            console.log('Touch zone debug:', !currentDebug ? 'enabled' : 'disabled')
          }
          break
        case 'Backquote':
          // Open Screenshot Manager with "`" or "~" key
          if (!this.scene.isActive('ScreenshotManager')) {
            console.log('📸 Opening Screenshot Manager (backtick)')
            this.scene.launch('ScreenshotManager')
          } else {
            this.scene.stop('ScreenshotManager')
          }
          break
      }
    })
  }

  private addScreenshotButton() {
    // Only add on native platforms (iOS/Android)
    if (!Capacitor.isNativePlatform()) {
      return // Skip on web
    }

    const w = this.scale.width
    const h = this.scale.height

    // Create floating camera button in top-right corner
    const buttonSize = 50
    const padding = 10
    const x = w - buttonSize / 2 - padding
    const y = buttonSize / 2 + padding

    // Background circle
    const bg = this.add.circle(x, y, buttonSize / 2, 0x4444ff, 0.8)
    bg.setScrollFactor(0)
    bg.setDepth(10000)

    // Camera icon (using text emoji for simplicity)
    const icon = this.add.text(x, y, '📸', {
      fontSize: '24px'
    }).setOrigin(0.5)
    icon.setScrollFactor(0)
    icon.setDepth(10001)

    // Make interactive - launches automated screenshot sequence
    bg.setInteractive({ useHandCursor: true })
      .on('pointerdown', () => {
        bg.setFillStyle(0x3333dd, 0.9)
      })
      .on('pointerup', async () => {
        bg.setFillStyle(0x4444ff, 0.8)
        console.log('📸 Starting automated screenshot sequence (button)')

        // Launch ScreenshotManager and trigger automated sequence
        if (!this.scene.isActive('ScreenshotManager')) {
          this.scene.launch('ScreenshotManager')
          // Wait a frame for the scene to initialize
          this.time.delayedCall(100, () => {
            const screenshotScene = this.scene.get('ScreenshotManager') as any
            if (screenshotScene && screenshotScene.runAutoSequence) {
              screenshotScene.runAutoSequence()
            }
          })
        }
      })
      .on('pointerover', () => {
        bg.setFillStyle(0x5555ff, 0.9)
      })
      .on('pointerout', () => {
        bg.setFillStyle(0x4444ff, 0.8)
      })
  }

  private setupGameEventListeners() {
    this.gameController.on('gameStarted', (stats: any) => {
      console.log('Game started:', stats)

      // Clear any existing saved game since we're starting fresh
      GameStateSaver.clearSavedGame()

      // Track game start in analytics
      Analytics.trackGameStart()

      // Start time tracking
      this.gameStartTime = Date.now()
      this.currentGameTime = 0

      // Update time every second (only for non-Time Attack modes)
      if (this.gameTimeTimer) {
        this.gameTimeTimer.destroy()
      }

      if (this.selectedMode !== GameMode.TIME_ATTACK) {
        this.gameTimeTimer = this.time.addEvent({
          delay: 1000,
          callback: () => {
            this.currentGameTime = Math.floor((Date.now() - this.gameStartTime) / 1000)
            if (this.gameHUD && this.gameHUD.updateGameTime) {
              this.gameHUD.updateGameTime(this.currentGameTime)
            }
          },
          loop: true
        })
      }

      const SoundEffectConstructor = (globalThis as any).SoundEffect
      if (this.audioController && SoundEffectConstructor && SoundEffectConstructor.GAME_START) {
        this.audioController.playSound(SoundEffectConstructor.GAME_START)
      }
    })

    this.gameController.on('pairSpawned', (config: any) => {
      console.log('New pair spawned:', config)

      // Update next pieces preview in CellLegend (for topBottomHUD and portraitTopBottomHUD modes)
      if (this.cellLegend && this.cellLegend.updateNextPieces) {
        // Get next pairs from game controller
        let nextPairs: any[] = []
        if (this.gameController instanceof EndlessGameMode) {
          nextPairs = (this.gameController as EndlessGameMode).getNextPairs()
        } else if (this.gameController instanceof TimeAttackGameMode) {
          nextPairs = (this.gameController as TimeAttackGameMode).getNextPairs()
        }

        if (nextPairs && nextPairs.length > 0) {
          this.cellLegend.updateNextPieces(nextPairs)
        }
      }

      // Play subtle spawn sound (with safety check)
      const SoundEffectConstructor = (globalThis as any).SoundEffect
      if (this.audioController && SoundEffectConstructor && SoundEffectConstructor.CELL_DROP) {
        this.audioController.playSound(SoundEffectConstructor.CELL_DROP)
      }

      // Add gentle sparkle when new pair appears
      if (config && config.primary && config.secondary) {
        const centerX = this.scale.width / 2
        const topY = 50
        this.visualEffects.createParticleEffect(`spawn_${Date.now()}`, {
          x: centerX,
          y: topY,
          texture: 'sparkle_particle',
          count: 3,
          speed: { min: 10, max: 20 },
          scale: { start: 0.4, end: 0.1 },
          alpha: { start: 0.6, end: 0.0 },
          duration: 400,
          color: 0xffffff
        } as any)
      }
    })

    this.gameController.on('matchProcessed', (result: any) => {
      console.log('Match processed:', result)

      // Trigger visual and audio effects for matches
      if (result.matches && result.matches.length > 0) {
        // Play match sound based on total cells matched
        const totalCells = result.matches.reduce((sum: number, match: any) => sum + (match.cells?.length || 0), 0)
        this.audioController.playMatchSound(totalCells, result.matches.length > 1)
        // Staggered per-match visual effect timing for combos
        const COMBO_STAGGER_MS = 140
        result.matches.forEach((match: any, index: number) => {
          this.time.delayedCall(index * COMBO_STAGGER_MS, () => {
            if (match.cells && match.cells.length > 0) {
              // Calculate center position of the match
              const centerX = match.cells.reduce((sum: number, cell: any) => sum + cell.x, 0) / match.cells.length
              const centerY = match.cells.reduce((sum: number, cell: any) => sum + cell.y, 0) / match.cells.length

              // Convert grid coordinates to screen coordinates
              const gridScale = this.gridSprite.scaleX
              const cellSize = 96 * gridScale
              const gridStartX = this.gridSprite.x - (this.gridSprite.width * gridScale) / 2
              const gridStartY = this.gridSprite.y - (this.gridSprite.height * gridScale) / 2
              const screenX = gridStartX + (centerX + 0.5) * cellSize
              const screenY = gridStartY + (centerY + 0.5) * cellSize

              // Create match effect
              this.visualEffects.createMatchEffect(screenX, screenY, match.type, match.cells.length)

              // Special effects for larger matches
              if (match.cells.length >= 5) {
                this.visualEffects.createFloatingText(screenX, screenY - 30, 'BIG MATCH!', '#ff6b6b', 20)
              }

              // Healing effect + subtle sound
              this.visualEffects.createHealingEffect(screenX, screenY)
              this.audioController.playCellSound(match.type, 'heal')
            }
          })
        })

        // Combo effect appears after last staggered match (or immediately if single)
        if (result.matches.length > 1) {
          const centerX = this.scale.width / 2
          const centerY = this.scale.height / 2
          this.time.delayedCall((result.matches.length - 1) * COMBO_STAGGER_MS, () => {
            this.visualEffects.createComboEffect(centerX, centerY, result.matches.length)
          })
        }

        // Dramatic chain pacing indicator (single floating text if chainLevel > 1)
        if (result.chainLevel > 1) {
          const chainTextDelay = Math.max(0, (result.matches.length - 1) * COMBO_STAGGER_MS + 50)
          this.time.delayedCall(chainTextDelay, () => {
            this.visualEffects.createFloatingText(
              this.scale.width / 2,
              this.scale.height / 2 - 120,
              `CHAIN x${result.chainLevel}!`,
              '#ffd700',
              30
            )
          })
        }
      }
    })

    this.gameController.on('engraftmentLevelUp', (level: number) => {
      console.log('Engraftment level up:', level)

      // Skip level-up animations for modes that stay at level 3
      const isTimeAttack = this.gameController instanceof TimeAttackGameMode
      const isEndlessEasy = this.gameController instanceof EndlessGameMode &&
        SettingsManager.getInstance().getDifficulty() === 'Easy'

      if (isTimeAttack || isEndlessEasy) {
        console.log('Skipping level-up animation for static-level mode')
        return
      }

      // Auto-save after level up
      this.performAutoSave()

      // Play level up sound (with safety check)
      const SoundEffectConstructor = (globalThis as any).SoundEffect
      if (this.audioController && SoundEffectConstructor && SoundEffectConstructor.LEVEL_UP) {
        this.audioController.playSound(SoundEffectConstructor.LEVEL_UP)
      }

      // Show level up animation
      //      this.visualEffects.createLevelUpEffect()
      this.visualEffects.createFloatingText(
        this.scale.width / 2,
        this.scale.height / 3,
        `LEVEL UP!`,
        '#ffd700',
        28
      )
    })

    this.gameController.on('gameOver', (stats: any) => {
      console.log('Game over:', stats)

      // Clear saved game since the game has ended
      GameStateSaver.clearSavedGame()

      // Stop time tracking
      if (this.gameTimeTimer) {
        this.gameTimeTimer.destroy()
        this.gameTimeTimer = undefined
      }

      // Use timeElapsed from stats (already calculated by game controller with pause handling)
      const gameTime = stats.timeElapsed || 0

      // Track game end in analytics
      Analytics.trackGameEnd(stats.score || 0, gameTime, stats.longestChain || 0)

      // Determine game mode for storage
      const storageMode = this.selectedMode === GameMode.TIME_ATTACK ? 'timeAttack' : 'endless'

      // Update persistent stats with mode-specific high score
      const { newHighScore, newLongestChain } = GameStorage.updateAfterGame(
        stats.score || 0,
        gameTime,
        stats.longestChain || 0,
        storageMode
      )

      // Submit to Game Center (async, don't wait)
      this.submitToGameCenter(stats.score || 0, stats.longestChain || 0)

      // Add indicators to stats for the dialog
      stats.gameTime = gameTime
      stats.newHighScore = newHighScore
      stats.newLongestChain = newLongestChain

      // Stop gameplay music and play game over sound (with safety check)
      this.audioController.stopMusic()
      const SoundEffectConstructor = (globalThis as any).SoundEffect
      if (this.audioController && SoundEffectConstructor && SoundEffectConstructor.GAME_OVER) {
        this.audioController.playSound(SoundEffectConstructor.GAME_OVER)
      }

      // Show game over dialog
      this.showGameOverDialog(stats)

      // Start game over music (with safety check)
      this.time.delayedCall(500, () => {
        const MusicTrackConstructor = (globalThis as any).MusicTrack
        if (this.audioController && MusicTrackConstructor && MusicTrackConstructor.GAME_OVER) {
          this.audioController.playMusic(MusicTrackConstructor.GAME_OVER)
        }
      })
    })

    // Listen for pause event to show pause dialog
    this.gameController.on('gamePaused', () => {
      console.log('Game paused - showing dialog')

      // Auto-save when game is paused
      this.performAutoSave()

      this.showPauseDialog()
    })

    this.gameController.on('victory', (stats: any) => {
      console.log('Victory:', stats)

      // Clear saved game since the game has ended
      GameStateSaver.clearSavedGame()

      // Stop gameplay music and play victory sound (with safety check)
      this.audioController.stopMusic()
      const SoundEffectConstructor = (globalThis as any).SoundEffect
      if (this.audioController && SoundEffectConstructor && SoundEffectConstructor.VICTORY) {
        this.audioController.playSound(SoundEffectConstructor.VICTORY)
      }

      // Show victory screen with celebration
      // this.visualEffects.createScreenFlash(0xffd700, 0.8, 600) // COMMENTED OUT - too jarring

      this.time.delayedCall(300, () => {
        this.visualEffects.createLevelUpEffect() // Reuse the celebration effect

        this.visualEffects.createFloatingText(
          this.scale.width / 2,
          this.scale.height / 2 - 50,
          'VICTORY!',
          '#ffd700',
          42
        )

        this.time.delayedCall(800, () => {
          this.visualEffects.createFloatingText(
            this.scale.width / 2,
            this.scale.height / 2 + 20,
            'TRANSPLANT SUCCESSFUL!',
            '#2ecc71',
            24
          )
        })

        this.time.delayedCall(1500, () => {
          this.visualEffects.createFloatingText(
            this.scale.width / 2,
            this.scale.height / 2 + 70,
            `Final Score: ${stats.score || 0}`,
            '#ffffff',
            18
          )
        })

        // Start victory music (with safety check)
        this.time.delayedCall(2000, () => {
          const MusicTrackConstructor = (globalThis as any).MusicTrack
          if (this.audioController && MusicTrackConstructor && MusicTrackConstructor.VICTORY) {
            this.audioController.playMusic(MusicTrackConstructor.VICTORY)
          }
        })
      })
    })

    // Time Attack mode: listen for time updates to show countdown
    this.gameController.on('timeUpdated', (timeRemaining: number) => {
      if (this.gameHUD && this.gameHUD.updateGameTime) {
        // For Time Attack, show countdown timer instead of elapsed time
        this.gameHUD.updateGameTime(timeRemaining)
      }
    })

    // Time Attack mode: listen for time bonus awards
    this.gameController.on('timeBonusAwarded', (bonus: number) => {
      if (this.visualEffects) {
        this.visualEffects.createFloatingText(
          this.scale.width / 2,
          this.scale.height / 4,
          `+${bonus}s`,
          '#4ecdc4',
          28
        )
      }
    })
  }

  private updateCellSprites() {
    const gridScale = this.gridSprite.scaleX
    const cellSize = 96 * gridScale
    const gridStartX = this.gridSprite.x - (this.gridSprite.width * gridScale) / 2
    const gridStartY = this.gridSprite.y - (this.gridSprite.height * gridScale) / 2

    // Remove sprites for cells that no longer exist
    const spritesToRemove: string[] = []
    for (const [cellId, pooledSprite] of this.cellSprites) {
      const cell = this.findCellById(cellId)
      if (!cell) {
        // Determine texture key from sprite to return to correct pool
        const textureKey = pooledSprite.sprite.texture.key
        this.poolManager.releaseSprite(textureKey, pooledSprite)
        spritesToRemove.push(cellId)
      }
    }
    spritesToRemove.forEach(cellId => this.cellSprites.delete(cellId))

    // Update or create sprites for existing cells
    const allCells = this.grid.getAllCells()
    const jiggleEnabled = this.settingsManager.isCellJiggleEnabled()
    const isPuzzleMode = this.selectedMode === GameMode.PUZZLE

    // Get current falling pair to exclude from grid updates
    const currentPair = this.gameController.getCurrentPair()
    const fallingCellIds = new Set<string>()
    if (currentPair) {
      const { primary, secondary } = currentPair.getCells()
      fallingCellIds.add(primary.id)
      fallingCellIds.add(secondary.id)
    }

    for (const cell of allCells) {
      // Skip cells that are part of the falling pair - they're handled by updateCurrentPairSprites
      if (fallingCellIds.has(cell.id)) {
        continue
      }

      let pooledSprite = this.cellSprites.get(cell.id)

      if (!pooledSprite) {
        // Get sprite from pool
        const cellX = gridStartX + (cell.x + 0.5) * cellSize
        const cellY = gridStartY + (cell.y + 0.5) * cellSize

        // Cancer cells always jiggle in Puzzle mode
        const shouldJiggle = jiggleEnabled || (isPuzzleMode && cell.type === CellType.CANCER)
        const textureKey = shouldJiggle ? `${cell.type}_blink` : `${cell.type}_idle`
        const animationKey = shouldJiggle ? `${cell.type}_blink` : null

        pooledSprite = this.poolManager.getSprite(textureKey)
        pooledSprite.configure(cellX, cellY, true)
          .setScale(cellSize / 128) // Assuming original sprite is 128px

        // Play animation immediately for jiggling cells
        if (animationKey) {
          // Use nextTick to ensure sprite is ready
          this.time.delayedCall(0, () => {
            if (pooledSprite && pooledSprite.sprite.anims) {
              pooledSprite.playAnimation(animationKey)
            }
          })
        }

        this.cellSprites.set(cell.id, pooledSprite)
      } else {
        // Update existing sprite position
        const cellX = gridStartX + (cell.x + 0.5) * cellSize
        const cellY = gridStartY + (cell.y + 0.5) * cellSize

        pooledSprite.sprite.setPosition(cellX, cellY)

        // Update animation based on cell state
        if (cell.markedForDeletion && !pooledSprite.sprite.anims.isPlaying) {
          pooledSprite.playAnimation(`${cell.type}_pop`)
        } else if (!cell.markedForDeletion) {
          // Cancer cells always jiggle in Puzzle mode
          const shouldJiggle = jiggleEnabled || (isPuzzleMode && cell.type === CellType.CANCER)
          if (shouldJiggle && pooledSprite.sprite.anims.currentAnim?.key !== `${cell.type}_blink`) {
            // Ensure blink animation is playing if not marked for deletion
            pooledSprite.playAnimation(`${cell.type}_blink`)
          }
        }
      }
    }
  }

  private updateCurrentPairSprites() {
    const currentPair = this.gameController.getCurrentPair()
    if (!currentPair) return

    const { primary, secondary } = currentPair.getCells()
    this.updateFallingCellSprite(primary)
    this.updateFallingCellSprite(secondary)
  }

  private updateFallingCellSprite(cell: any) {
    const gridScale = this.gridSprite.scaleX
    const cellSize = 96 * gridScale
    const gridStartX = this.gridSprite.x - (this.gridSprite.width * gridScale) / 2
    const gridStartY = this.gridSprite.y - (this.gridSprite.height * gridScale) / 2

    let pooledSprite = this.cellSprites.get(cell.id)

    if (!pooledSprite) {
      const cellX = gridStartX + (cell.x + 0.5) * cellSize
      const cellY = gridStartY + (cell.y + 0.5) * cellSize

      // Check if cell jiggle is enabled
      const jiggleEnabled = this.settingsManager.isCellJiggleEnabled()
      const textureKey = jiggleEnabled ? `${cell.type}_blink` : `${cell.type}_idle`
      const animationKey = jiggleEnabled ? `${cell.type}_blink` : null

      console.log(`[FallingCell] jiggleEnabled=${jiggleEnabled}, textureKey=${textureKey}, animationKey=${animationKey}`)
      console.log(`[FallingCell] Animation exists: ${this.anims.exists(animationKey || '')}`)

      pooledSprite = this.poolManager.getSprite(textureKey)
      pooledSprite.configure(cellX, cellY, true)
        .setScale(cellSize / 128)

      // Only play animation if jiggle is enabled
      if (animationKey) {
        console.log(`[FallingCell] Playing animation: ${animationKey}`)
        pooledSprite.playAnimation(animationKey)
        console.log(`[FallingCell] Animation playing: ${pooledSprite.sprite.anims.isPlaying}`)
      }

      console.log(`[FallingCell] Created falling cell sprite for ${cell.type} with ${jiggleEnabled ? 'blink' : 'idle'} texture`)

      this.cellSprites.set(cell.id, pooledSprite)
    } else {
      const cellX = gridStartX + (cell.x + 0.5) * cellSize
      const cellY = gridStartY + (cell.y + 0.5) * cellSize
      pooledSprite.sprite.setPosition(cellX, cellY)

      // Ensure animation keeps playing while falling (if jiggle is enabled)
      const jiggleEnabled = this.settingsManager.isCellJiggleEnabled()
      if (jiggleEnabled) {
        const animationKey = `${cell.type}_blink`
        if (!pooledSprite.sprite.anims.isPlaying || pooledSprite.sprite.anims.currentAnim?.key !== animationKey) {
          pooledSprite.playAnimation(animationKey)
        }
      }
    }
  }

  private findCellById(cellId: string) {
    const allCells = this.grid.getAllCells()
    return allCells.find(cell => cell.id === cellId)
  }

  /**
   * Update performance metrics with current game state data
   */
  private updatePerformanceMetrics(): void {
    if (!this.performanceMonitor) return

    // Count active cells in the grid
    let activeCells = 0
    for (let x = 0; x < 6; x++) {
      for (let y = 0; y < 12; y++) {
        if (this.grid.getCell(x, y) !== null) {
          activeCells++
        }
      }
    }

    // Count falling pairs
    const currentPair = this.gameController.getCurrentPair()
    const fallingPairs = currentPair ? 1 : 0

    // Count particle effects (estimate from visual effects controller)
    const particles = this.visualEffects ?
      this.children.list.filter(obj => obj.type === 'Emitter').length : 0

    // Count active audio sources (simplified estimate)
    const audioSources = 0 // Will implement proper counting later

    // Update performance monitor with game metrics
    this.performanceMonitor.setGameMetrics({
      activeCells,
      fallingPairs,
      particles,
      audioSources
    })

    // Log pool statistics periodically (every 5 seconds)
    if (this.poolManager && this.time.now % 5000 < 100) {
      const poolStats = this.poolManager.getTotalStats()
      console.log('🏊 Pool Stats:', poolStats)
    }
  }

  /**
   * Shows the game over dialog with stats and action buttons
   */
  private showGameOverDialog(stats: any): void {
    const centerX = this.scale.width / 2
    const centerY = this.scale.height / 2

    // Get responsive dialog size
    const dialogSize = ResponsiveLayout.getDialogSize(this, 450, 400)
    const responsive = ResponsiveLayout.getConfig(this)

    // Get high score and total stats
    const gameStats = GameStorage.getStats()

    // Create semi-transparent overlay
    const overlay = this.add.rectangle(
      0,
      0,
      this.scale.width,
      this.scale.height,
      0x000000,
      0.8
    )
    overlay.setOrigin(0, 0)
    overlay.setDepth(10000)
    // Don't make overlay interactive - it would block button clicks

    // Create dialog background (make it taller to fit more info)
    const dialogWidth = dialogSize.width
    const dialogHeight = dialogSize.height
    const dialogBg = this.add.graphics()
    dialogBg.fillStyle(0x2c3e50, 1)
    dialogBg.fillRoundedRect(centerX - dialogWidth / 2, centerY - dialogHeight / 2, dialogWidth, dialogHeight, 15)
    dialogBg.lineStyle(4, 0x34495e, 1)
    dialogBg.strokeRoundedRect(centerX - dialogWidth / 2, centerY - dialogHeight / 2, dialogWidth, dialogHeight, 15)
    dialogBg.setDepth(10001)

    // Make dialog background interactive to catch clicks (prevents clicking through)
    dialogBg.setInteractive(
      new Phaser.Geom.Rectangle(centerX - dialogWidth / 2, centerY - dialogHeight / 2, dialogWidth, dialogHeight),
      Phaser.Geom.Rectangle.Contains
    )

    // Game Over title
    const titleFontSize = ResponsiveLayout.getFontSize(this, 42)
    const title = this.add.text(centerX, centerY - dialogHeight * 0.375, 'GAME OVER', {
      fontSize: `${titleFontSize}px`,
      fontFamily: '"M PLUS Rounded 1c", Arial, sans-serif',
      color: '#e74c3c',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 4
    }).setOrigin(0.5).setDepth(10002)

    // Current Score with "NEW!" indicator if high score
    const scoreColor = stats.newHighScore ? '#f39c12' : '#ffffff'
    const scoreLabel = stats.newHighScore ? 'NEW HIGH SCORE!' : 'Score'
    const scoreFontSize = ResponsiveLayout.getFontSize(this, stats.newHighScore ? 32 : 28)
    const scoreText = this.add.text(centerX, centerY - dialogHeight * 0.225, `${scoreLabel}: ${stats.score || 0}`, {
      fontSize: `${scoreFontSize}px`,
      fontFamily: '"M PLUS Rounded 1c", Arial, sans-serif',
      color: scoreColor,
      fontStyle: stats.newHighScore ? 'bold' : 'normal',
      stroke: '#000000',
      strokeThickness: 3
    }).setOrigin(0.5).setDepth(10002)

    // Time played this round
    const timeFontSize = ResponsiveLayout.getFontSize(this, 22)
    const timeText = this.add.text(centerX, centerY - dialogHeight * 0.125, `Time: ${GameStorage.formatTime(stats.gameTime || 0)}`, {
      fontSize: `${timeFontSize}px`,
      fontFamily: '"M PLUS Rounded 1c", Arial, sans-serif',
      color: '#95a5a6',
      stroke: '#000000',
      strokeThickness: 2
    }).setOrigin(0.5).setDepth(10002)

    // Best Chain with "NEW!" indicator if record
    const chainColor = stats.newLongestChain ? '#f39c12' : '#3498db'
    const chainLabel = stats.newLongestChain ? 'NEW! Best Chain' : 'Best Chain'
    const chainFontSize = ResponsiveLayout.getFontSize(this, stats.newLongestChain ? 26 : 24)
    const chainText = this.add.text(centerX, centerY - dialogHeight * 0.0375, `${chainLabel}: ${stats.longestChain || 0}`, {
      fontSize: `${chainFontSize}px`,
      fontFamily: '"M PLUS Rounded 1c", Arial, sans-serif',
      color: chainColor,
      fontStyle: stats.newLongestChain ? 'bold' : 'normal',
      stroke: '#000000',
      strokeThickness: 2
    }).setOrigin(0.5).setDepth(10002)

    // Separator line
    const separator = this.add.graphics()
    separator.lineStyle(2, 0x7f8c8d, 0.5)
    separator.lineBetween(centerX - dialogWidth * 0.4, centerY + dialogHeight * 0.0375, centerX + dialogWidth * 0.4, centerY + dialogHeight * 0.0375)
    separator.setDepth(10002)

    // Mode-specific high score display
    const modeHighScore = this.selectedMode === GameMode.TIME_ATTACK
      ? gameStats.timeAttackHighScore
      : gameStats.endlessHighScore
    const modeLabel = this.selectedMode === GameMode.TIME_ATTACK
      ? 'Time Attack Best Score'
      : 'Endless Best Score'

    const statsFontSize = ResponsiveLayout.getFontSize(this, 18)
    const highScoreText = this.add.text(centerX, centerY + dialogHeight * 0.1, `${modeLabel}: ${modeHighScore}`, {
      fontSize: `${statsFontSize}px`,
      fontFamily: '"M PLUS Rounded 1c", Arial, sans-serif',
      color: '#ecf0f1',
      stroke: '#000000',
      strokeThickness: 2
    }).setOrigin(0.5).setDepth(10002)

    // Total time played
    const totalTimeText = this.add.text(centerX, centerY + dialogHeight * 0.1625, `Total Time: ${GameStorage.formatTime(gameStats.totalTimePlayed)}`, {
      fontSize: `${statsFontSize}px`,
      fontFamily: '"M PLUS Rounded 1c", Arial, sans-serif',
      color: '#ecf0f1',
      stroke: '#000000',
      strokeThickness: 2
    }).setOrigin(0.5).setDepth(10002)

    const buttonFontSize = ResponsiveLayout.getFontSize(this, 22)
    const buttonsY = centerY + dialogHeight * 0.325

    // Share button (left)
    const shareButton = this.add.text(centerX, buttonsY, '📤 Share', {
      fontSize: `${buttonFontSize}px`,
      fontFamily: '"M PLUS Rounded 1c", Arial, sans-serif',
      color: '#ffffff',
      backgroundColor: '#3498db',
      padding: { x: 16, y: 10 }
    }).setOrigin(0.5).setDepth(10002)

    shareButton.setInteractive({ useHandCursor: true })
    shareButton.on('pointerover', () => {
      shareButton.setScale(1.1)
      shareButton.setStyle({ backgroundColor: '#5dade2' })
    })
    shareButton.on('pointerout', () => {
      shareButton.setScale(1.0)
      shareButton.setStyle({ backgroundColor: '#3498db' })
    })
    shareButton.on('pointerup', async () => {
      console.log('Share clicked - opening share dialog')
      const iOS = iOSIntegration.getInstance()
      const gameMode = this.selectedMode === GameMode.TIME_ATTACK ? 'timeAttack' : 'endless'
      await iOS.shareGameOverStats({
        score: stats.score || 0,
        gameMode: gameMode,
        gameTime: GameStorage.formatTime(stats.gameTime || 0),
        longestChain: stats.longestChain || 0,
        newHighScore: stats.newHighScore || false
      })
    })

    // Try Again button (center)
    const tryAgainButton = this.add.text(centerX, buttonsY, 'Try Again', {
      fontSize: `${buttonFontSize}px`,
      fontFamily: '"M PLUS Rounded 1c", Arial, sans-serif',
      color: '#ffffff',
      backgroundColor: '#27ae60',
      padding: { x: 16, y: 10 }
    }).setOrigin(0.5).setDepth(10002)

    tryAgainButton.setInteractive({ useHandCursor: true })
    tryAgainButton.on('pointerover', () => {
      tryAgainButton.setScale(1.1)
      tryAgainButton.setStyle({ backgroundColor: '#2ecc71' })
    })
    tryAgainButton.on('pointerout', () => {
      tryAgainButton.setScale(1.0)
      tryAgainButton.setStyle({ backgroundColor: '#27ae60' })
    })
    tryAgainButton.on('pointerup', () => {
      console.log('Try Again clicked - restarting game')
      // Set flag to restart music when game restarts
      this.registry.set('startMusic', true)
      // Restart the game
      this.scene.restart()
    })

    // Quit button (right)
    const quitButton = this.add.text(centerX, buttonsY, 'Quit', {
      fontSize: `${buttonFontSize}px`,
      fontFamily: '"M PLUS Rounded 1c", Arial, sans-serif',
      color: '#ffffff',
      backgroundColor: '#c0392b',
      padding: { x: 16, y: 10 }
    }).setOrigin(0.5).setDepth(10002)

    quitButton.setInteractive({ useHandCursor: true })
    quitButton.on('pointerover', () => {
      quitButton.setScale(1.1)
      quitButton.setStyle({ backgroundColor: '#e74c3c' })
    })
    quitButton.on('pointerout', () => {
      quitButton.setScale(1.0)
      quitButton.setStyle({ backgroundColor: '#c0392b' })
    })
    quitButton.on('pointerup', () => {
      console.log('Quit clicked - returning to title')
      // Stop music before returning to title
      if (this.audioController) {
        this.audioController.stopMusic()
      }
      // Return to title screen
      this.scene.start('Title')
    })

    // Lay out buttons evenly along the bottom of the dialog without overlap
    const buttons = [shareButton, tryAgainButton, quitButton]
    const availableWidth = dialogWidth * 0.9
    const minSpacing = responsive.isPortrait ? 16 : 20
    const baseTotalWidth = buttons.reduce((sum, btn) => sum + btn.width, 0)
    const maxScaledTotal = availableWidth - minSpacing * (buttons.length - 1)
    const scaleFactor = Math.min(1, Math.max(0.6, maxScaledTotal / baseTotalWidth))

    buttons.forEach((btn) => btn.setScale(scaleFactor))

    const totalScaledWidth = buttons.reduce((sum, btn) => sum + btn.displayWidth, 0)
    const spacing = Math.max(minSpacing, (availableWidth - totalScaledWidth) / (buttons.length - 1))
    let currentX = centerX - (totalScaledWidth + spacing * (buttons.length - 1)) / 2

    buttons.forEach((btn) => {
      btn.setPosition(currentX + btn.displayWidth / 2, buttonsY)
      currentX += btn.displayWidth + spacing
    })

    // Animate dialog entrance
    overlay.setAlpha(0)
    dialogBg.setAlpha(0)
    title.setAlpha(0).setY(centerY - dialogHeight * 0.425)
    scoreText.setAlpha(0)
    timeText.setAlpha(0)
    chainText.setAlpha(0)
    separator.setAlpha(0)
    highScoreText.setAlpha(0)
    totalTimeText.setAlpha(0)
    shareButton.setAlpha(0)
    tryAgainButton.setAlpha(0)
    quitButton.setAlpha(0)

    this.tweens.add({
      targets: overlay,
      alpha: 1,
      duration: 300,
      ease: 'Power2'
    })

    this.tweens.add({
      targets: dialogBg,
      alpha: 1,
      duration: 400,
      delay: 100,
      ease: 'Power2'
    })

    this.tweens.add({
      targets: title,
      alpha: 1,
      y: centerY - dialogHeight * 0.375,
      duration: 500,
      delay: 200,
      ease: 'Back.easeOut'
    })

    this.tweens.add({
      targets: [scoreText, timeText, chainText],
      alpha: 1,
      duration: 400,
      delay: 400,
      ease: 'Power2'
    })

    this.tweens.add({
      targets: [separator, highScoreText, totalTimeText],
      alpha: 1,
      duration: 400,
      delay: 500,
      ease: 'Power2'
    })

    this.tweens.add({
      targets: [shareButton, tryAgainButton, quitButton],
      alpha: 1,
      duration: 400,
      delay: 600,
      ease: 'Power2'
    })
  }

  /**
   * Show pause dialog with menu options
   */
  private showPauseDialog(): void {
    // Don't show multiple dialogs
    if (this.pauseDialogElements.length > 0) {
      return
    }

    const { width, height } = this.scale
    const centerX = width / 2
    const centerY = height / 2

    // Get responsive dialog size
    const dialogSize = ResponsiveLayout.getDialogSize(this, 450, 480)
    const responsive = ResponsiveLayout.getConfig(this)

    // Create overlay
    const overlay = this.add.rectangle(0, 0, width, height, 0x000000, 0.8)
      .setOrigin(0, 0)
      .setDepth(10000)
      .setInteractive()

    // Create dialog background (increased height to fit all buttons)
    const dialogWidth = dialogSize.width
    const dialogHeight = dialogSize.height
    const dialogBg = this.add.graphics()
      .setDepth(10001)
      .fillStyle(0x2c3e50, 1)
      .fillRoundedRect(centerX - dialogWidth / 2, centerY - dialogHeight / 2, dialogWidth, dialogHeight, 16)
      .lineStyle(4, 0x3498db, 1)
      .strokeRoundedRect(centerX - dialogWidth / 2, centerY - dialogHeight / 2, dialogWidth, dialogHeight, 16)

    // Title
    const titleFontSize = ResponsiveLayout.getFontSize(this, 48)
    const title = this.add.text(centerX, centerY - dialogHeight * 0.354, 'PAUSED', {
      fontSize: `${titleFontSize}px`,
      color: '#ffffff',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    })
      .setOrigin(0.5)
      .setDepth(10002)

    // Resume button (primary action - green)
    const buttonSize = ResponsiveLayout.getButtonSize(this, 200, 48)
    const primaryButtonFontSize = ResponsiveLayout.getFontSize(this, 24)
    const resumeButton = this.add.text(centerX, centerY - dialogHeight * 0.146, 'Resume', {
      fontSize: `${primaryButtonFontSize}px`,
      color: '#ffffff',
      backgroundColor: '#27ae60',
      padding: { x: 30, y: 12 },
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      fixedWidth: buttonSize.width,
      align: 'center'
    })
      .setOrigin(0.5)
      .setDepth(10002)
      .setInteractive({ useHandCursor: true })

    // Save button (secondary action - blue)
    const secondaryButtonFontSize = ResponsiveLayout.getFontSize(this, 18)
    const saveButton = this.add.text(centerX, centerY - dialogHeight * 0.01, 'Continue Later', {
      fontSize: `${secondaryButtonFontSize}px`,
      color: '#ffffff',
      backgroundColor: '#3498db',
      padding: { x: 20, y: 10 },
      fontFamily: 'Arial, sans-serif',
      fixedWidth: buttonSize.width,
      align: 'center'
    })
      .setOrigin(0.5)
      .setDepth(10002)
      .setInteractive({ useHandCursor: true })
      .setOrigin(0.5)
      .setDepth(10002)
      .setInteractive({ useHandCursor: true })

    // Secondary menu buttons
    const menuButtonFontSize = ResponsiveLayout.getFontSize(this, 20)
    const buttonY = centerY + dialogHeight * 0.125
    const buttonSpacing = dialogHeight * 0.115

    const settingsButton = this.add.text(centerX, buttonY, 'Settings', {
      fontSize: `${menuButtonFontSize}px`,
      color: '#ffffff',
      backgroundColor: '#34495e',
      padding: { x: 25, y: 10 },
      fontFamily: 'Arial, sans-serif',
      fixedWidth: buttonSize.width,
      align: 'center'
    })
      .setOrigin(0.5)
      .setDepth(10002)
      .setInteractive({ useHandCursor: true })

    const helpButton = this.add.text(centerX, buttonY + buttonSpacing, 'Help', {
      fontSize: `${menuButtonFontSize}px`,
      color: '#ffffff',
      backgroundColor: '#34495e',
      padding: { x: 25, y: 10 },
      fontFamily: 'Arial, sans-serif',
      fixedWidth: buttonSize.width,
      align: 'center'
    })
      .setOrigin(0.5)
      .setDepth(10002)
      .setInteractive({ useHandCursor: true })

    const quitButton = this.add.text(centerX, buttonY + buttonSpacing * 2, 'Quit', {
      fontSize: `${menuButtonFontSize}px`,
      color: '#ffffff',
      backgroundColor: '#e74c3c',
      padding: { x: 25, y: 10 },
      fontFamily: 'Arial, sans-serif',
      fixedWidth: buttonSize.width,
      align: 'center'
    })
      .setOrigin(0.5)
      .setDepth(10002)
      .setInteractive({ useHandCursor: true })

    // Store references for cleanup
    this.pauseDialogElements = [overlay, dialogBg, title, resumeButton, saveButton, settingsButton, helpButton, quitButton]

    // Resume button - unpause and close dialog
    resumeButton.on('pointerover', () => {
      resumeButton.setStyle({ backgroundColor: '#2ecc71' })
    })
    resumeButton.on('pointerout', () => {
      resumeButton.setStyle({ backgroundColor: '#27ae60' })
    })
    resumeButton.on('pointerup', () => {
      this.resumeFromPause()
    })

    // Save button - manually save game state and quit to title
    saveButton.on('pointerover', () => {
      saveButton.setStyle({ backgroundColor: '#5dade2' })
    })
    saveButton.on('pointerout', () => {
      saveButton.setStyle({ backgroundColor: '#3498db' })
    })
    saveButton.on('pointerup', () => {
      // Save the game directly (even when paused)
      const modeName = this.selectedMode === GameMode.TIME_ATTACK ? 'Time Attack' : 'Endless'
      const success = GameStateSaver.saveGame(this.gameController, modeName)

      if (success) {
        // Mark this as a manual quit (not a background event)
        localStorage.setItem('manualQuit', 'true')
        localStorage.setItem('lastGameSaveTime', Date.now().toString())

        // Show visual feedback briefly
        saveButton.setText('✓ Saved!')
        saveButton.setStyle({ backgroundColor: '#27ae60' })

        console.log('Game saved manually via Continue Later button')
      } else {
        // Show error feedback
        saveButton.setText('❌ Save Failed')
        saveButton.setStyle({ backgroundColor: '#e74c3c' })
        console.error('Failed to save game via Continue Later button')
      }

      // Wait a moment then return to title screen
      this.time.delayedCall(500, () => {
        // Clean up pause dialog
        this.pauseDialogElements.forEach(el => el.destroy())
        this.pauseDialogElements = []

        // Stop the game scene and return to title
        this.scene.stop()
        this.scene.start('Title')
      })
    })

    // Settings button - navigate to Settings scene with return context
    settingsButton.on('pointerover', () => {
      settingsButton.setStyle({ backgroundColor: '#4a5f7f' })
    })
    settingsButton.on('pointerout', () => {
      settingsButton.setStyle({ backgroundColor: '#34495e' })
    })
    settingsButton.on('pointerup', () => {
      this.pauseDialogElements.forEach(el => el.destroy())
      this.pauseDialogElements = []
      // Launch Settings scene without stopping Game scene
      this.scene.pause()
      this.scene.launch('Settings', { returnTo: 'Game', paused: true })
    })

    // Help button - navigate to Help scene with return context
    helpButton.on('pointerover', () => {
      helpButton.setStyle({ backgroundColor: '#4a5f7f' })
    })
    helpButton.on('pointerout', () => {
      helpButton.setStyle({ backgroundColor: '#34495e' })
    })
    helpButton.on('pointerup', () => {
      this.pauseDialogElements.forEach(el => el.destroy())
      this.pauseDialogElements = []
      // Launch Help scene without stopping Game scene
      this.scene.pause()
      this.scene.launch('Help', { returnTo: 'Game', paused: true })
    })

    // Quit button - show confirmation dialog
    quitButton.on('pointerover', () => {
      quitButton.setStyle({ backgroundColor: '#c0392b' })
    })
    quitButton.on('pointerup', () => {
      quitButton.setStyle({ backgroundColor: '#e74c3c' })
    })
    quitButton.on('pointerup', () => {
      this.pauseDialogElements.forEach(el => el.destroy())
      this.pauseDialogElements = []
      this.showQuitConfirmationDialog()
    })

    // Entrance animations
    overlay.setAlpha(0)
    dialogBg.setAlpha(0)
    title.setAlpha(0).setY(centerY - 190)
    resumeButton.setAlpha(0)
    saveButton.setAlpha(0)
    settingsButton.setAlpha(0)
    helpButton.setAlpha(0)
    quitButton.setAlpha(0)

    this.tweens.add({
      targets: overlay,
      alpha: 1,
      duration: 300,
      ease: 'Power2'
    })

    this.tweens.add({
      targets: dialogBg,
      alpha: 1,
      duration: 400,
      delay: 100,
      ease: 'Power2'
    })

    this.tweens.add({
      targets: title,
      alpha: 1,
      y: centerY - 170,
      duration: 500,
      delay: 200,
      ease: 'Back.easeOut'
    })

    this.tweens.add({
      targets: resumeButton,
      alpha: 1,
      duration: 400,
      delay: 400,
      ease: 'Power2'
    })

    this.tweens.add({
      targets: saveButton,
      alpha: 1,
      duration: 400,
      delay: 450,
      ease: 'Power2'
    })

    this.tweens.add({
      targets: [settingsButton, helpButton, quitButton],
      alpha: 1,
      duration: 400,
      delay: 500,
      ease: 'Power2'
    })
  }

  /**
   * Show quit confirmation dialog
   */
  private showQuitConfirmationDialog(): void {
    const { width, height } = this.scale
    const centerX = width / 2
    const centerY = height / 2

    // Create overlay
    const overlay = this.add.rectangle(0, 0, width, height, 0x000000, 0.8)
      .setOrigin(0, 0)
      .setDepth(10003)
      .setInteractive()

    // Create dialog background
    const dialogBg = this.add.graphics()
      .setDepth(10004)
      .fillStyle(0x2c3e50, 1)
      .fillRoundedRect(centerX - 225, centerY - 200, 450, 400, 16)
      .lineStyle(4, 0xe74c3c, 1)
      .strokeRoundedRect(centerX - 225, centerY - 200, 450, 400, 16)

    // Title
    const title = this.add.text(centerX, centerY - 150, 'QUIT GAME?', {
      fontSize: '42px',
      color: '#ffffff',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    })
      .setOrigin(0.5)
      .setDepth(10005)

    // Subtitle
    const subtitle = this.add.text(centerX, centerY - 100, 'Choose an option:', {
      fontSize: '18px',
      color: '#bdc3c7',
      fontFamily: 'Arial, sans-serif'
    })
      .setOrigin(0.5)
      .setDepth(10005)

    // Consistent button width for all buttons
    const buttonWidth = 200

    // Try Again button (restart game)
    const tryAgainButton = this.add.text(centerX, centerY - 20, 'Try Again', {
      fontSize: '20px',
      color: '#ffffff',
      backgroundColor: '#27ae60',
      padding: { x: 25, y: 10 },
      fontFamily: 'Arial, sans-serif',
      fixedWidth: buttonWidth,
      align: 'center'
    })
      .setOrigin(0.5)
      .setDepth(10005)
      .setInteractive({ useHandCursor: true })

    // Return to Menu button
    const menuButton = this.add.text(centerX, centerY + 40, 'Return to Menu', {
      fontSize: '20px',
      color: '#ffffff',
      backgroundColor: '#e74c3c',
      padding: { x: 25, y: 10 },
      fontFamily: 'Arial, sans-serif',
      fixedWidth: buttonWidth,
      align: 'center'
    })
      .setOrigin(0.5)
      .setDepth(10005)
      .setInteractive({ useHandCursor: true })

    // Cancel button
    const cancelButton = this.add.text(centerX, centerY + 100, 'Cancel', {
      fontSize: '20px',
      color: '#ffffff',
      backgroundColor: '#34495e',
      padding: { x: 25, y: 10 },
      fontFamily: 'Arial, sans-serif',
      fixedWidth: buttonWidth,
      align: 'center'
    })
      .setOrigin(0.5)
      .setDepth(10005)
      .setInteractive({ useHandCursor: true })

    // Store references for cleanup
    const confirmElements = [overlay, dialogBg, title, subtitle, tryAgainButton, menuButton, cancelButton]

    // Try Again button - restart game
    tryAgainButton.on('pointerover', () => {
      tryAgainButton.setStyle({ backgroundColor: '#2ecc71' })
    })
    tryAgainButton.on('pointerout', () => {
      tryAgainButton.setStyle({ backgroundColor: '#27ae60' })
    })
    tryAgainButton.on('pointerup', () => {
      confirmElements.forEach(el => el.destroy())
      // Set flag to restart music when game restarts
      this.registry.set('startMusic', true)
      // Restart the game
      this.scene.restart()
    })

    // Return to Menu button - quit to title
    menuButton.on('pointerover', () => {
      menuButton.setStyle({ backgroundColor: '#c0392b' })
    })
    menuButton.on('pointerout', () => {
      menuButton.setStyle({ backgroundColor: '#e74c3c' })
    })
    menuButton.on('pointerup', () => {
      confirmElements.forEach(el => el.destroy())

      // Clear saved game when quitting
      GameStateSaver.clearSavedGame()

      // Stop music before returning to title
      if (this.audioController) {
        this.audioController.stopMusic()
      }
      // Return to title screen
      this.scene.start('Title')
    })

    // Cancel button - back to pause dialog
    cancelButton.on('pointerover', () => {
      cancelButton.setStyle({ backgroundColor: '#4a5f7f' })
    })
    cancelButton.on('pointerout', () => {
      cancelButton.setStyle({ backgroundColor: '#34495e' })
    })
    cancelButton.on('pointerup', () => {
      confirmElements.forEach(el => el.destroy())
      // Show pause dialog again
      this.showPauseDialog()
    })

    // Entrance animations
    overlay.setAlpha(0)
    dialogBg.setAlpha(0)
    title.setAlpha(0).setY(centerY - 170)
    subtitle.setAlpha(0)
    tryAgainButton.setAlpha(0)
    menuButton.setAlpha(0)
    cancelButton.setAlpha(0)

    this.tweens.add({
      targets: overlay,
      alpha: 1,
      duration: 300,
      ease: 'Power2'
    })

    this.tweens.add({
      targets: dialogBg,
      alpha: 1,
      duration: 400,
      delay: 100,
      ease: 'Power2'
    })

    this.tweens.add({
      targets: title,
      alpha: 1,
      y: centerY - 150,
      duration: 500,
      delay: 200,
      ease: 'Back.easeOut'
    })

    this.tweens.add({
      targets: subtitle,
      alpha: 1,
      duration: 400,
      delay: 300,
      ease: 'Power2'
    })

    this.tweens.add({
      targets: [tryAgainButton, menuButton, cancelButton],
      alpha: 1,
      duration: 400,
      delay: 400,
      ease: 'Power2'
    })
  }

  /**
   * Resume from pause and play unpause sound
   */
  private resumeFromPause(): void {
    // Destroy pause dialog if it exists
    this.pauseDialogElements.forEach(el => el.destroy())
    this.pauseDialogElements = []

    // Resume game directly (don't use handleInput('pause') as that calls pauseGame, not resumeGame)
    this.gameController.resumeGame()

    const SoundEffectConstructor = (globalThis as any).SoundEffect
    if (this.audioController && SoundEffectConstructor && SoundEffectConstructor.UNPAUSE) {
      this.audioController.playSound(SoundEffectConstructor.UNPAUSE)
    }
  }

  /**
   * Submit scores and achievements to Game Center
   */
  private async submitToGameCenter(score: number, longestChain: number): Promise<void> {
    // Check if Game Center is available and authenticated
    if (!GameCenter.isGameCenterAvailable() || !GameCenter.isPlayerAuthenticated()) {
      console.log('Game Center not available or not authenticated - skipping submission')
      return
    }

    try {
      // Get current stats for leaderboard submissions
      const stats = GameStorage.getStats()

      // Submit mode-specific high score to appropriate leaderboard
      if (this.selectedMode === GameMode.TIME_ATTACK) {
        await GameCenter.submitTimeAttackHighScore(score)
        console.log('Submitted Time Attack high score:', score)
      } else {
        // Default to Endless mode (includes ENDLESS and any future modes)
        await GameCenter.submitEndlessHighScore(score)
        console.log('Submitted Endless high score:', score)
      }

      // Submit universal leaderboards (same across all modes)
      await GameCenter.submitLongestChain(longestChain)
      await GameCenter.submitTotalTime(stats.totalTimePlayed)
      await GameCenter.submitGamesPlayed(stats.gamesPlayed)

      // Check and submit score achievements
      await GameCenter.checkScoreAchievements(score)

      // Check and submit chain achievements
      await GameCenter.checkChainAchievements(longestChain)

      // Check and submit games played achievements
      await GameCenter.checkGamesPlayedAchievements(stats.gamesPlayed)

      // Check and submit time-based achievements
      await GameCenter.checkTimeAchievements(stats.totalTimePlayed)

      console.log('Game Center submission complete')
    } catch (error) {
      console.error('Error submitting to Game Center:', error)
    }
  }

  /**
   * Setup auto-save system for mid-game state persistence
   */
  private setupAutoSave(): void {
    // Clear any existing auto-save timer
    if (this.autoSaveInterval) {
      this.autoSaveInterval.destroy()
    }

    // Setup periodic auto-save
    this.autoSaveInterval = this.time.addEvent({
      delay: this.autoSaveFrequency,
      callback: () => {
        this.performAutoSave()
      },
      loop: true
    })

    console.log('Auto-save system initialized with', this.autoSaveFrequency / 1000, 'second intervals')
  }

  /**
   * Performs an auto-save if game is in a valid state
   */
  private performAutoSave(): void {
    try {
      // Only auto-save during active gameplay
      const gameState = this.gameController.getState()
      if (gameState !== GameState.PLAYING && gameState !== GameState.FALLING && gameState !== GameState.MATCHING) {
        return
      }

      // Don't save too frequently to avoid performance issues
      const now = Date.now()
      if (now - this.lastAutoSave < 5000) { // Minimum 5 seconds between saves
        return
      }

      // Determine mode name for save
      const modeName = this.selectedMode === GameMode.TIME_ATTACK ? 'Time Attack' : 'Endless'

      // Perform the save
      const success = GameStateSaver.saveGame(this.gameController, modeName)
      if (success) {
        this.lastAutoSave = now
        // Track when we last saved for auto-resume after backgrounding
        localStorage.setItem('lastGameSaveTime', now.toString())
        console.log('Auto-save completed at', new Date().toLocaleTimeString())
      }
    } catch (error) {
      console.error('Auto-save failed:', error)
    }
  }

  /**
   * Setup browser lifecycle events for emergency saves
   */
  private setupBrowserLifecycleEvents(): void {
    // Save when user navigates away or closes the tab
    const handleBeforeUnload = () => {
      this.performEmergencySave('beforeunload')
    }

    // Save when browser/tab loses focus (mobile app backgrounding)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('App going to background - pausing game, music, and saving')

        // Pause the game to stop time and gameplay
        const gameState = this.gameController.getState()
        if (gameState === GameState.PLAYING || gameState === GameState.FALLING || gameState === GameState.MATCHING) {
          this.gameController.pauseGame()
          // Don't show pause dialog yet - wait until they return to foreground
        }

        // Pause music when app goes to background
        if (this.audioController) {
          this.audioController.pauseMusic()
        }
        this.performEmergencySave('visibilitychange')
      } else {
        console.log('App returning to foreground')

        // Show pause dialog if game was paused due to backgrounding
        const gameState = this.gameController.getState()
        if (gameState === GameState.PAUSED) {
          // Small delay to ensure scene is ready
          this.time.delayedCall(100, () => {
            this.showPauseDialog()
          })
        }

        // Resume music when app comes back to foreground
        if (this.audioController) {
          this.audioController.resumeMusic()
        }
      }
    }

    // Save when window loses focus
    const handleBlur = () => {
      console.log('Window lost focus - pausing game, music, and saving')

      // Pause the game to stop time and gameplay
      const gameState = this.gameController.getState()
      if (gameState === GameState.PLAYING || gameState === GameState.FALLING || gameState === GameState.MATCHING) {
        this.gameController.pauseGame()
      }

      // Pause music when window loses focus
      if (this.audioController) {
        this.audioController.pauseMusic()
      }
      this.performEmergencySave('blur')
    }

    // Resume music when window gains focus
    const handleFocus = () => {
      console.log('Window gained focus')

      // Show pause dialog if game was paused due to losing focus
      const gameState = this.gameController.getState()
      if (gameState === GameState.PAUSED) {
        // Small delay to ensure scene is ready
        this.time.delayedCall(100, () => {
          this.showPauseDialog()
        })
      }

      // Resume music when window gains focus
      if (this.audioController) {
        this.audioController.resumeMusic()
      }
    }

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('blur', handleBlur)
    window.addEventListener('focus', handleFocus)

    // Store references for cleanup
    this.browserEventCleanup = () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('blur', handleBlur)
      window.removeEventListener('focus', handleFocus)
    }

    console.log('Browser lifecycle event listeners added for auto-save and music pause/resume')
  }

  /**
   * Performs an emergency save when the app is about to be backgrounded/closed
   */
  private performEmergencySave(reason: string): void {
    try {
      // Only save during active gameplay
      const gameState = this.gameController.getState()
      if (gameState !== GameState.PLAYING && gameState !== GameState.FALLING && gameState !== GameState.MATCHING) {
        return
      }

      // Determine mode name for save
      const modeName = this.selectedMode === GameMode.TIME_ATTACK ? 'Time Attack' : 'Endless'

      // Force save regardless of timing
      const success = GameStateSaver.saveGame(this.gameController, modeName)
      if (success) {
        // Store timestamp for auto-resume detection
        localStorage.setItem('lastGameSaveTime', Date.now().toString())
        console.log(`Emergency save completed due to ${reason}`)
      }
    } catch (error) {
      console.error(`Emergency save failed (${reason}):`, error)
    }
  }

  shutdown() {
    // Cleanup auto-save system
    if (this.autoSaveInterval) {
      this.autoSaveInterval.destroy()
      this.autoSaveInterval = undefined
    }

    // Cleanup browser event listeners
    if (this.browserEventCleanup) {
      this.browserEventCleanup()
      this.browserEventCleanup = undefined
    }

    // Clean up performance monitor
    if (this.performanceMonitor) {
      this.performanceMonitor.destroy()
    }

    // Clean up object pools
    if (this.poolManager) {
      this.poolManager.destroy()
    }

    // Clean up HUD
    if (this.gameHUD) {
      this.gameHUD.destroy()
    }

    // Clean up cell legend
    if (this.cellLegend) {
      this.cellLegend.destroy()
    }

    // Clean up visual effects
    if (this.visualEffects) {
      this.visualEffects.destroy()
    }

    // Clean up audio
    if (this.audioController) {
      this.audioController.destroy()
    }

    // Clean up mobile systems
    if (this.touchController) {
      this.touchController.destroy()
    }

    // End telemetry session
    this.telemetryService.endSession()

    // Clean up mobile optimizer
    if (this.mobileOptimizer) {
      this.mobileOptimizer.destroy()
    }

    // Clean up resize listener
    this.scale.off('resize', this.handleResize, this)
  }

  /**
   * Setup screenshot scenario - trigger specific game events for screenshots
   */
  private setupScreenshotScenario(): void {
    const scenario = ScreenshotMode.config.scenario
    console.log('🎬 [Game] Setting up screenshot scenario:', scenario)

    // For now, the pre-filled grid is enough for screenshots
    // The grid is already populated with the scenario-specific layout
    // Future: Could trigger specific animations or overlays here

    if (ScreenshotMode.shouldTriggerBigMatch()) {
      console.log('🎬 [Game] Big Match scenario - grid pre-filled with 5+ match pattern')
    } else if (ScreenshotMode.shouldTriggerCombo()) {
      console.log('🎬 [Game] Combo scenario - grid pre-filled with cascade pattern')
    } else if (ScreenshotMode.shouldTriggerLevelUp()) {
      console.log('🎬 [Game] Level Up scenario - grid pre-filled for multiple matches')
    } else {
      console.log('🎬 [Game] Screenshot scenario ready:', scenario)
    }
  }
}

